import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import { FiThumbsUp, FiThumbsDown, FiLock, FiImage, FiPlus } from 'react-icons/fi';
import { getTalents, reactTalent } from '../../../../store/talents/talentsSlice';
import { TalentsGrid, TalentCard } from './TalentsStyle';
import { backendUrl } from '../../../../utilities/axios';
import { SkeletonCreatorCard, NoData } from '../../../shared/WebsiteFeedback';

const TalentsGridSection = ({ category, addPath }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast({ position: 'top', duration: 3000 });
  const { data: talentsData, isLoading } = useSelector(state => state.talents);
  const auth = useSelector(state => state.auth);
  const [optimistic, setOptimistic] = useState({});
  const userId = auth.user?._id;

  useEffect(() => {
    dispatch(getTalents({
      page: 1, size: 30, query: '',
      filter: JSON.stringify({ status: 'approved', category }),
      sort: '-score',
    }));
  }, [dispatch, category]);

  const talents = talentsData?.data || [];

  const myReaction = (talent) => {
    if (optimistic[talent._id]) return optimistic[talent._id];
    if (talent.likedBy?.some(id => id === userId)) return 'like';
    if (talent.dislikedBy?.some(id => id === userId)) return 'dislike';
    return null;
  };

  const handleReact = (e, talent, action) => {
    e.preventDefault();
    e.stopPropagation();
    if (!auth.user) {
      toast({ description: t('website.talents.login_required', 'يجب تسجيل الدخول أولاً للتقييم'), status: 'warning' });
      navigate('/login');
      return;
    }
    const current = myReaction(talent);
    const next = current === action ? null : action;
    setOptimistic(p => ({ ...p, [talent._id]: next }));
    dispatch(reactTalent({ _id: talent._id, action })).unwrap().catch(() => {
      setOptimistic(p => { const n = { ...p }; delete n[talent._id]; return n; });
      toast({ description: t('general.something_went_wrong', 'حدث خطأ، حاول مرة أخرى'), status: 'error' });
    });
  };

  return (
    <>
      {isLoading ? (
        <TalentsGrid>
          {Array.from({ length: 8 }, (_, i) => <SkeletonCreatorCard key={i} />)}
        </TalentsGrid>
      ) : talents.length === 0 ? (
        <NoData />
      ) : (
        <TalentsGrid>
          {talents.map(talent => {
            const reaction = myReaction(talent);
            const image = talent.images?.[0] ? `${backendUrl}/talents/${talent.images[0]}` : null;
            return (
              <TalentCard key={talent._id} as={Link} to={`/talent/${talent._id}`}>
                <div className="cover">
                  {image ? <img src={image} alt={talent.name} /> : <div className="no-image"><FiImage /></div>}
                </div>
                <div className="card-body">
                  <div className="talent-name">{talent.name}</div>
                  <div className="talent-desc">{talent.description}</div>
                  <div className="react-row">
                    <button
                      className={`react-btn${reaction === 'like' ? ' active-like' : ''}${!auth.user ? ' locked' : ''}`}
                      onClick={e => handleReact(e, talent, 'like')}
                    >
                      {!auth.user ? <FiLock /> : <FiThumbsUp />} {talent.likes}
                    </button>
                    <button
                      className={`react-btn${reaction === 'dislike' ? ' active-dislike' : ''}${!auth.user ? ' locked' : ''}`}
                      onClick={e => handleReact(e, talent, 'dislike')}
                    >
                      {!auth.user ? <FiLock /> : <FiThumbsDown />} {talent.dislikes}
                    </button>
                  </div>
                </div>
              </TalentCard>
            );
          })}
        </TalentsGrid>
      )}

      <Link to={addPath} style={{
        position: 'fixed', insetInlineEnd: 24, bottom: 24,
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '12px 20px', background: '#7C3AED', color: '#fff',
        borderRadius: 999, textDecoration: 'none', fontWeight: 700, fontSize: 13.5,
        boxShadow: '0 6px 24px rgba(124,58,237,0.4)',
      }}>
        <FiPlus /> {t('website.talents.add_btn', 'تقديم طلب')}
      </Link>
    </>
  );
};

export default TalentsGridSection;
